import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ObservationModal from "../components/ObservationModal";
import FullCheckInHistoryModal from "../components/FullCheckInHistoryModal";
import { useAuth } from "../components/TokenHandler";

import FolderOutlined from "@mui/icons-material/FolderOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import ErrorOutline from "@mui/icons-material/ErrorOutline";

const modalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1250,
    maxHeight: "85vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 0,
    zIndex: 1100,
    overflowY: "auto",
    borderRadius: 0,
};

const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    bgcolor: "#00A7A0",
    color: "white",
    padding: "16px 24px",
};

const BuildingCheckInHistoryModal = ({ open, onClose, building }) => {
    const [isObservationModalOpen, setObservationModalOpen] = useState(false);
    const [isFullHistoryOpen, setFullHistoryOpen] = useState(false);
    const [isHistoryVisible, setHistoryVisible] = useState(true);
    const [checkInData, setCheckInData] = useState([]);
    const [selectedCheckInId, setSelectedCheckInId] = useState(null);

    const { keycloak: kc } = useAuth();

    // Manage history visibility state
    useEffect(() => {
        setHistoryVisible(!isObservationModalOpen && !isFullHistoryOpen);
    }, [isObservationModalOpen, isFullHistoryOpen]);

    const openObservationModal = () => {
        setObservationModalOpen(true);
    };

    const closeObservationModal = () => {
        setObservationModalOpen(false);
    };

    const openFullHistoryModal = (id) => {
        setSelectedCheckInId(id);
        setFullHistoryOpen(true);
    };

    const closeFullHistoryModal = () => {
        setFullHistoryOpen(false);
        fetchCheckInData();
        setSelectedCheckInId(null);
    };

    const closeAllModals = () => {
        setFullHistoryOpen(false);
        onClose();
        setCheckInData([]);
        setSelectedCheckInId(null);
    };

    const fetchCheckInData = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/get_checkin_toplevel2?building_unique_id=${building.building_unique_id}&corp_unique_id=${building.corp_unique_id}`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                let data = await response.json();

                // Sort the data by date in ascending order (earliest first)
                data.sort((b, a) => new Date(a.date) - new Date(b.date));

                setCheckInData(data);
            } else {
                console.error("Failed to fetch check-in data");
            }
        } catch (error) {
            console.error("Error fetching check-in data:", error);
        }
    };


    useEffect(() => {
        if (open && building) {
            fetchCheckInData();
        }
    }, [open, building]);

    return (
        <>
            {/*  Show history modal only if it's marked as visible */}
            {open && isHistoryVisible && (
                <Modal open={open} onClose={onClose} aria-labelledby="modal-title">
                    <Box sx={modalStyle}>
                        <Box sx={headerStyle}>
                            <Typography id="modal-title" variant="h6">
                                {building.legal_name}
                            </Typography>
                            <IconButton onClick={onClose} style={{ color: "white" }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ padding: "16px" }}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                                <Button
                                    variant="contained"
                                    sx={{ backgroundColor: "#2c6d91", "&:hover": { backgroundColor: "#1f4f6d" } }}
                                    onClick={openObservationModal}
                                >
                                    Add New Check-In thread
                                </Button>
                            </Box>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Lastest Status</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Creator</TableCell>
                                        <TableCell>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {checkInData.map((checkIn) => (
                                        <TableRow key={checkIn.id} onClick={() => openFullHistoryModal(checkIn.id)} style={{ cursor: "pointer" }}>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        padding: "2px 8px",
                                                        borderRadius: "12px",
                                                        fontWeight: "bold",
                                                        fontSize: "0.875rem",
                                                        backgroundColor: checkIn.checkin_status.toLowerCase().startsWith("held and attended")
                                                            ? "#DFF2BF" // Light green for success
                                                            : ["not scheduled", "scheduled", "scheduled - corp"].includes(checkIn.checkin_status.toLowerCase())
                                                                ? "#D9EAF7" // Light blue for in progress
                                                                : checkIn.checkin_status.toLowerCase() === "customer no show"
                                                                    ? "#FFBABA" // Light red for error
                                                                    : "#EAEAEA", // Default grey for unknown or other
                                                        color: checkIn.checkin_status.toLowerCase().startsWith("held and attended")
                                                            ? "#4F8A10" // Dark green text
                                                            : ["not scheduled", "scheduled", "scheduled - corp"].includes(checkIn.checkin_status.toLowerCase())
                                                                ? "#1A5F85" // Dark blue text
                                                                : checkIn.checkin_status.toLowerCase() === "customer no show"
                                                                    ? "#D8000C" // Dark red text
                                                                    : "#6C757D", // Default grey text
                                                    }}
                                                >
                                                    {/* Folder Icon */}
                                                    <FolderOutlined sx={{ fontSize: "1rem", color: "#6C757D" }} />

                                                    {/* Status Icons */}
                                                    {checkIn.checkin_status.toLowerCase().startsWith("held and attended") && (
                                                        <CheckCircle sx={{ fontSize: "1rem", color: "#4F8A10" }} />
                                                    )}
                                                    {["not scheduled", "scheduled", "scheduled - corp"].includes(checkIn.checkin_status.toLowerCase()) && (
                                                        <HourglassEmpty sx={{ fontSize: "1rem", color: "#1A5F85" }} />
                                                    )}
                                                    {checkIn.checkin_status.toLowerCase() === "customer no show" && (
                                                        <ErrorOutline sx={{ fontSize: "1rem", color: "#D8000C" }} />
                                                    )}

                                                    {/* Status Text */}
                                                    {checkIn.checkin_status}
                                                </Box>
                                            </TableCell>




                                            <TableCell>{checkIn.subject}</TableCell>
                                            <TableCell>{checkIn.creator}</TableCell>
                                            <TableCell>{new Date(checkIn.date).toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Modal>
            )}

            {/*  Observation Modal - Hides history modal while open */}
            <ObservationModal
                isOpen={isObservationModalOpen}
                onClose={closeObservationModal}
                building={building}
                refreshData={fetchCheckInData}
            />

            {/*  Full Check-In History Modal - Hides history modal while open */}
            <FullCheckInHistoryModal
                open={isFullHistoryOpen}
                onClose={closeAllModals}
                onBack={closeFullHistoryModal}
                checkInId={selectedCheckInId}
                building={building}
            />
        </>
    );
};

export default BuildingCheckInHistoryModal;
